/**
 * Build-time feature discovery.
 *
 * `import.meta.glob` is resolved by Vite at build time, so adding
 * `src/features/<slug>/manifest.ts` is sufficient to add a chapter's routes,
 * navigation, export registrations and drill declarations to the application. No
 * shared file is edited. This module is the ONLY place that knows features exist,
 * and it is not a file any chapter agent needs to touch.
 *
 * The three things a chapter agent gets for free by adding a manifest:
 *   routes           mounted at /<slug>/<path> by src/app/routes/
 *   export census    enumerated here and asserted by tools/export-census.mjs
 *   i18n namespace   locale/<locale>/<slug>.json merged by src/i18n/
 */

/* eslint-disable bata/no-message-concatenation --
 * The census functions below build human-readable GAP DESCRIPTIONS for CI output and for
 * the failure message a chapter agent will read. They are developer diagnostics, addressed
 * to whoever broke the build, and G6 governs strings shown to a PLANNER. This module
 * renders nothing: it contains no JSX and calls no translator, so the exemption cannot
 * quietly widen to cover UI copy.
 */

import {
  ADR_BLOCKS, DEFAULT_RAIL_GROUP, FEATURE_SLUGS, MANDATORY_EXPORT_FORMATS, isDataRoute,
  type ChapterIcon, type FeatureManifest, type FeatureRoute, type FeatureSlug, type NavEntry,
  type RailGroup,
} from './manifest';
import { isScreenGuide, type ScreenGuide } from './guides';

type ManifestModule = { default: FeatureManifest };

/**
 * Eager because the manifests are metadata only — no component code is pulled in,
 * since `route.component` is itself a lazy import. The shell needs the whole route
 * table to render navigation and to resolve a deep link on first paint.
 */
const modules = import.meta.glob<ManifestModule>('./*/manifest.ts', { eager: true });

function slugFromPath(filePath: string): string {
  const m = /^\.\/([^/]+)\/manifest\.ts$/.exec(filePath);
  return m?.[1] ?? '';
}

function collect(): FeatureManifest[] {
  const out: FeatureManifest[] = [];
  for (const [filePath, mod] of Object.entries(modules)) {
    const manifest = mod.default;
    const dir = slugFromPath(filePath);
    if (!manifest) {
      throw new Error(`${filePath} has no default export. A feature manifest must \`export default defineFeature({ … })\`.`);
    }
    if (manifest.slug !== dir) {
      throw new Error(`${filePath} declares slug "${manifest.slug}" but lives in "${dir}". They must match — the locale namespace merge and the ADR block check both key off the directory name. See CLAUDE.md.`);
    }
    if (!(FEATURE_SLUGS as readonly string[]).includes(manifest.slug)) {
      throw new Error(`"${manifest.slug}" is not one of the nine chapter namespaces (${FEATURE_SLUGS.join(', ')}). See CLAUDE.md namespace ownership.`);
    }
    if (manifest.localeNamespace !== manifest.slug) {
      throw new Error(`${filePath}: localeNamespace "${manifest.localeNamespace}" must equal slug "${manifest.slug}".`);
    }
    out.push(manifest);
  }
  return out.sort((a, b) => FEATURE_SLUGS.indexOf(a.slug) - FEATURE_SLUGS.indexOf(b.slug));
}

export const features: readonly FeatureManifest[] = collect();

/** Absolute path a route is mounted at. `/<slug>` for the feature index. */
export function routePath(slug: FeatureSlug, route: FeatureRoute): string {
  return route.path === '' ? `/${slug}` : `/${slug}/${route.path.replace(/^\//, '')}`;
}

export interface ResolvedRoute {
  readonly slug: FeatureSlug;
  readonly path: string;
  readonly route: FeatureRoute;
  readonly adrBlock: readonly [number, number];
}

export const routes: readonly ResolvedRoute[] = features.flatMap((f) =>
  f.routes.map((route) => ({
    slug: f.slug,
    path: routePath(f.slug, route),
    route,
    adrBlock: ADR_BLOCKS[f.slug],
  })));

/**
 * G2 test d — the export census, at runtime. The type system already makes a
 * `DataRoute` without `exports` impossible; this catches the case where the type
 * was defeated, and is what the Playwright census asserts against.
 */
export interface ExportCensusRow {
  readonly routePath: string;
  readonly surfaces: readonly string[];
  readonly exportId: string | null;
  readonly formats: readonly string[];
  readonly tableViewReachable: boolean;
}

export function exportCensus(): readonly ExportCensusRow[] {
  return routes.map(({ path, route }) => ({
    routePath: path,
    surfaces: route.surfaces,
    exportId: isDataRoute(route) ? route.exports.id : null,
    formats: isDataRoute(route) ? route.exports.formats : [],
    tableViewReachable: isDataRoute(route) ? route.tableViewReachable === true : false,
  }));
}

/**
 * Rows that would fail G2 test d. Empty is the only passing result.
 *
 * Takes the route list as a parameter so `src/features/census.test.ts` can feed it a
 * PLANTED violating manifest and assert that the census reports it. A census that has
 * only ever been run against conforming input has not been shown to work — the same
 * reasoning as tools/lint-proof.mjs.
 */
export function exportCensusGapsFor(routeList: readonly ResolvedRoute[]): readonly string[] {
  const gaps: string[] = [];
  for (const { path, route } of routeList) {
    const dataSurfaces = route.surfaces.filter((s) => s === 'table' || s === 'grid' || s === 'chart' || s === 'tile');
    if (dataSurfaces.length === 0) continue;
    if (!isDataRoute(route)) {
      gaps.push(`${path} renders ${dataSurfaces.join('/')} but declares no export handler (G2 test d)`);
      continue;
    }
    // ADR 9010 as amended on 2026-10-01 (DEC-98): every export is CSV AND Excel, never an
    // image. Both are required, and any other format declared is a gap, so neither can be
    // dropped, nor a picture added, by a manifest edit that nobody asked for.
    const declared = route.exports.formats as readonly string[];
    for (const format of MANDATORY_EXPORT_FORMATS) {
      if (!declared.includes(format)) {
        gaps.push(`${path} does not offer ${format.toUpperCase()} — every table, grid and chart leaves as CSV and Excel (ADR 9010)`);
      }
    }
    for (const format of declared) {
      if (!(MANDATORY_EXPORT_FORMATS as readonly string[]).includes(format)) {
        gaps.push(`${path} offers ${format.toUpperCase()} — exports are CSV and Excel, never an image (ADR 9010)`);
      }
    }
    if (route.surfaces.includes('chart') && route.tableViewReachable !== true) {
      gaps.push(`${path} renders a chart without declaring tableViewReachable (design/09 §12.10)`);
    }
  }
  return gaps;
}

export const exportCensusGaps = (): readonly string[] => exportCensusGapsFor(routes);

/** G2 test f — the drill census. */
export function drillCensusGapsFor(routeList: readonly ResolvedRoute[]): readonly string[] {
  const gaps: string[] = [];
  for (const { path, route } of routeList) {
    if (!isDataRoute(route)) continue;
    const d = route.drill;
    if ('terminal' in d && (!d.reason || d.reason.trim().length < 10)) {
      gaps.push(`${path} is a terminal aggregate with no documented reason (G2 test f)`);
    }
  }
  return gaps;
}

export const drillCensusGaps = (): readonly string[] => drillCensusGapsFor(routes);

/** Navigation tree for the shell, ordered. */
export function navigation(): readonly (NavEntry & { slug: FeatureSlug })[] {
  return features
    .flatMap((f) => (f.navigation ?? []).map((n) => ({ ...n, slug: f.slug })))
    .sort((a, b) => a.order - b.order);
}

/** One entry per chapter, its own screens nested underneath, in chapter order. */
export interface ChapterNav {
  readonly slug: FeatureSlug;
  readonly titleKey: string;
  /** Short label for the header's chapter row — see `FeatureManifest.navLabelKey`. */
  readonly navLabelKey: string;
  /** `FeatureManifest.icon`, when the chapter declares one. */
  readonly icon?: ChapterIcon;
  readonly entries: readonly NavEntry[];
  /** `FeatureManifest.disabled`, normalised to a boolean for the shell. */
  readonly disabled: boolean;
  /**
   * `FeatureManifest.railGroup`, normalised to `DEFAULT_RAIL_GROUP` when absent — the same
   * pattern as `disabled`, so the shell never has to know what the default is.
   */
  readonly railGroup: RailGroup;
}

/**
 * Chapter-grouped navigation for the two-row header (design/09 §6): row one is one
 * button per chapter, row two is the active chapter's own screens. Replaces the flat
 * 30-entry list `navigation()` used to hand the shell, which forced the header into a
 * horizontally-scrolling strip.
 */
export function chapterNavigation(): readonly ChapterNav[] {
  return features
    .filter((f) => (f.navigation ?? []).length > 0)
    .map((f) => ({
      slug: f.slug,
      titleKey: f.titleKey,
      navLabelKey: f.navLabelKey ?? f.titleKey,
      ...(f.icon === undefined ? {} : { icon: f.icon }),
      entries: [...(f.navigation ?? [])].sort((a, b) => a.order - b.order),
      disabled: f.disabled === true,
      railGroup: f.railGroup ?? DEFAULT_RAIL_GROUP,
    }));
}

/**
 * The screens a list of entries leads to — every entry without children, and the children
 * of every entry that has them, in order.
 *
 * `NavEntry.children` groups the views of ONE question under the screen that asks it (the
 * in-season markdown's run, output and explanation). The parent is a heading in the rail, not
 * a destination of its own, so "which screen is the planner on" and "which screens can the
 * palette offer" are both answered over the leaves. A child inherits its parent's `disabled`:
 * a withheld screen does not become reachable through its sub-views.
 */
export function screensOf(entries: readonly NavEntry[]): readonly NavEntry[] {
  return entries.flatMap((entry) => {
    if (entry.children === undefined || entry.children.length === 0) return [entry];
    const children = [...entry.children].sort((a, b) => a.order - b.order);
    return entry.disabled === true
      ? children.map((child) => ({ ...child, disabled: true as const }))
      : children;
  });
}

/**
 * A chapter with nothing a planner can open.
 *
 * Either the manifest says so outright, or every entry it has is individually disabled —
 * which amounts to the same thing from row one, and is the case a chapter arrives at one
 * screen at a time without anyone editing the chapter-level flag. Left to `disabled` alone
 * that chapter rendered as a working button that did nothing at all when pressed: no
 * navigation, no preview, no `title`, and no visual difference from a chapter that works.
 * A control that silently does nothing is worse than one that says it is not available yet.
 *
 * `entries.length > 0` guards the vacuous case: `every` is true of an empty list, and a
 * chapter with no navigation at all is not a chapter with nothing on offer.
 *
 * Lives here rather than in the header because two surfaces now ask the question — the
 * chapter row in `AppChrome` and the chapter index at `/` — and a withheld chapter that
 * two surfaces disagree about is worse than either answer.
 */
export function notOffered(chapter: ChapterNav): boolean {
  return chapter.disabled
    || (chapter.entries.length > 0 && chapter.entries.every((e) => e.disabled === true));
}

/**
 * One screen's full explanation, for the Documentation screen — bata-ui#132 step 6, and since
 * ADR 9044 either a paragraph (`docSectionKey`) or a five-part guide with its tour (`guide`).
 * Exactly one of the two is set: a chapter that declares `guides` documents its routes there.
 */
export interface DocumentationSection {
  readonly slug: FeatureSlug;
  /** The route the section documents, so the Documentation screen can link to it. */
  readonly path: string;
  /** The route's own title, to head the section. */
  readonly titleKey: string;
  /** `FeatureRoute.docSectionKey` — the section's body, when it is one paragraph. */
  readonly docSectionKey?: string;
  /** `FeatureManifest.guides[path]` — the section's body, when it is a guide. */
  readonly guide?: ScreenGuide;
}

/** The guide a chapter declares for one of its routes, if it declares one. */
function guideOf(slug: FeatureSlug, route: FeatureRoute): ScreenGuide | undefined {
  const entry = features.find((feature) => feature.slug === slug)?.guides?.[route.path];
  return isScreenGuide(entry) ? entry : undefined;
}

/**
 * Every declared documentation section, in chapter order and then route order.
 *
 * The Documentation screen reads this rather than a list of its own, for the reason the rail
 * reads `chapterNavigation()`: a chapter that declares a section appears with no shared file
 * edited, and the screen cannot describe a route that does not exist.
 */
export function documentationSections(): readonly DocumentationSection[] {
  return routes.flatMap(({ slug, path, route }): DocumentationSection[] => {
    const guide = guideOf(slug, route);
    if (guide !== undefined) return [{ slug, path, titleKey: route.titleKey, guide }];
    return route.docSectionKey === undefined
      ? []
      : [{ slug, path, titleKey: route.titleKey, docSectionKey: route.docSectionKey }];
  });
}

/** The guide of the route mounted at `path` (a declared path, `:params` and all), if any. */
export function screenGuide(path: string): ScreenGuide | undefined {
  const match = routes.find((candidate) => candidate.path === path);
  return match === undefined ? undefined : guideOf(match.slug, match.route);
}

/** A route a guide names, by its declared path and its own title. */
export interface GuideLink {
  readonly path: string;
  readonly titleKey: string;
}

/** What Documentation states about a screen from the manifest rather than from prose. */
export interface GuideFacts {
  /** The screens its rows drill into — `drill.to` and `alsoTo`, those that are mounted. */
  readonly leadsTo: readonly GuideLink[];
  /** The screens whose rows drill into it. */
  readonly reachedFrom: readonly GuideLink[];
  /** The formats its export offers; empty for a screen that shows no aggregate. */
  readonly exportFormats: readonly string[];
}

function drillTargets(route: FeatureRoute): readonly string[] {
  if (!isDataRoute(route) || 'terminal' in route.drill) return [];
  return [route.drill.to, ...(route.drill.alsoTo ?? [])];
}

/**
 * A route its chapter exempts from guides (`{ exempt }` — not offered, or a redirect). Such a
 * screen is not one a planner reaches, so no guide points them at it.
 */
function isExempt(slug: FeatureSlug, route: FeatureRoute): boolean {
  const entry = features.find((feature) => feature.slug === slug)?.guides?.[route.path];
  return entry !== undefined && !isScreenGuide(entry);
}

/**
 * Derived, so it cannot drift from what the product does: a drill target no route mounts (a
 * level declared ahead of its screen) is left out rather than linked to a page that is not
 * there; a screen its chapter exempts as not offered is left out on either side, because a
 * manifest's `alsoTo` can name a screen the rail draws inert; and a screen's drill into itself
 * (a narrowing by query string) is not somewhere else.
 */
export function guideFacts(path: string): GuideFacts {
  const link = (target: string): GuideLink | null => {
    const found = routes.find((candidate) => candidate.path === target);
    return found === undefined || isExempt(found.slug, found.route)
      ? null
      : { path: found.path, titleKey: found.route.titleKey };
  };
  const unique = (links: readonly (GuideLink | null)[]): GuideLink[] => {
    const seen = new Set<string>();
    return links.filter((entry): entry is GuideLink => {
      if (entry === null || entry.path === path || seen.has(entry.path)) return false;
      seen.add(entry.path);
      return true;
    });
  };
  const self = routes.find((candidate) => candidate.path === path);
  return {
    leadsTo: unique(self === undefined ? [] : drillTargets(self.route).map(link)),
    reachedFrom: unique(routes
      .filter((candidate) => !isExempt(candidate.slug, candidate.route) && drillTargets(candidate.route).includes(path))
      .map((candidate) => ({ path: candidate.path, titleKey: candidate.route.titleKey }))),
    exportFormats: self !== undefined && isDataRoute(self.route) ? self.route.exports.formats : [],
  };
}

/** Locale namespaces to load — one per discovered feature, plus the shared core. */
export function localeNamespaces(): readonly string[] {
  return ['core', ...features.map((f) => f.localeNamespace)];
}
